import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import UserPanel from '../components/UserPanel/UserPanel';
import Login from '../components/LoginSignup/Login';
import OAuthCallback from '../components/LoginSignup/OAuthCallback';
import CompleteProfile from '../components/LoginSignup/CompleteProfile';
import Signup from '../components/LoginSignup/signup';
import PortalSelector from '../components/LoginSignup/PortalSelector';
import ForgotPassword from '../components/ForgotPassword/ForgotPassword';
import ResetPassword from '../components/ResetPassword/ResetPassword';
import Admin from '../components/Sidebar/Admin';
import Add from '../components/Add/Add';
import List from '../components/List/List';
import TableComponent from '../components/TableComponent/TableComponent';
import UserData from '../components/UserData/UserData';
import Reviews from '../components/Reviews/Reviews';
import UserReviews from '../components/UserReviews/UserReviews';
import About from '../components/About/About';
import AdminTable from '../components/AdminTable/AdminTable';
import SlotTable from '../components/TableShow/SlotTable';
import UsersList from '../components/UsersList/UsersList';

import Invoices from '../components/Invoice/Invoice';
import UserDashBoard from '../components/UserDashboard/UserDashboard';
import InvoiceList from '../components/InvoiceList/InvoiceList';
import InvoiceDetail from '../components/UserInvoiceList/UserInvoiceList';
import EditInvoice from '../components/EditInvoice/EditInvoice';
import UserInvoice from "../components/UserInvoice/UserInvoice";
import UserInvoices from "../components/UserInvoices/UserInvoices";
import UserInvoiceDetail from "../components/UserInvoiceDetail/UserInvoiceDetail";
import UserMessages from "../components/UserMessages/UserMessages";
import Graph from "../components/Graph/Graph";
import FoodDetail from "../components/FoodDetail/FoodDetail";
import Services from '../components/Services/Services';
import BlogDeatils from '../components/BlogDetails/BlogDetails';
import Recipes from "../components/Recipes/Recipes"
import Menu_Page from '../components/Menu Page/Menu_Page';
import AllFoods from '../components/AllFoods/AllFoods';
import Info from '../components/Info/Info';
import Settings from '../components/Settings/Settings';
import SubscriptionSuccess from '../components/Subscription/SubscriptionSuccess';
import SubscriptionCancel from '../components/Subscription/SubscriptionCancel';
import PlanSelection from '../components/Subscription/PlanSelection';
import KitchenDisplay from '../components/Kitchen/KitchenDisplay';
import StaffManagement from '../components/Staff/StaffManagement';
import ChefSettings from '../components/Staff/ChefSettings';
import Attendance from '../components/Staff/Attendance';
import StaffAttendance from '../components/Staff/StaffAttendance';
import SalarySlips from '../components/Staff/SalarySlips';
import Performance from '../components/Staff/Performance';
import InventoryDashboard from '../components/Inventory/InventoryDashboard';
import SmartPrep from '../components/SmartPrep/SmartPrep';
import RestaurantSelection from '../components/RestaurantSelection/RestaurantSelection';
import CreateBill from '../components/CreateBill/CreateBill';


const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_CLIENT_KEY);

function PrivateRoute({ element, ...rest }) {
  const token = localStorage.getItem("token");
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  
  // Block staff from accessing generic private user routes
  if (userDetails?.role === 'staff') {
    // Managers go to admin dashboard, chefs go to kitchen
    if (userDetails?.staffRole === 'manager') {
      return <Navigate to="/admin" />;
    }
    return <Navigate to="/admin/kitchen" />;
  }
  
  // Block admin from user routes
  if (userDetails?.role === 'admin') {
    return <Navigate to="/admin" />;
  }
  
  // Block owner - redirect to master panel
  if (userDetails?.role === 'owner') {
    const storedToken = localStorage.getItem("token");
    window.location.href = `http://localhost:3001/dashboard?token=${storedToken}`;
    return null;
  }

  return token ? element : <Navigate to="/login" />;
}

function AdminRoute({ element, ...rest }) {
  const token = localStorage.getItem("token");
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  // Allow admin, owner, and staff managers to access admin routes
  const isAllowed = token && (userDetails?.role === 'admin' || userDetails?.role === 'owner' ||
    (userDetails?.role === 'staff' && userDetails?.staffRole === 'manager'));
  return isAllowed ? element : <Navigate to="/login" />;
}

function PrivateKitchenRoute({ element, ...rest }) {
  const token = localStorage.getItem("token");
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const allowed = token && ['admin', 'owner', 'staff'].includes(userDetails?.role);
  // All staff roles (chef, waiter, manager) can access kitchen routes
  return allowed ? element : <Navigate to="/login" />;
}

function NonAdminRoute({ element, ...rest }) {
  const token = localStorage.getItem("token");
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  
  // If not authenticated, redirect to login
  if (!token) {
    return <Navigate to="/login" />;
  }
  
  // Block staff from accessing public/user routes
  if (userDetails?.role === 'staff') {
    // Managers go to admin dashboard, chefs go to kitchen
    if (userDetails?.staffRole === 'manager') {
      return <Navigate to="/admin" />;
    }
    return <Navigate to="/admin/kitchen" />;
  }

  // If admin tries to access user page, redirect to admin panel
  if (userDetails?.role === 'admin') {
    return <Navigate to="/admin" />;
  }
  
  // If owner tries to access user page, redirect to master panel
  if (userDetails?.role === 'owner') {
    window.location.href = `http://localhost:3001/dashboard?token=${token}`;
    return null;
  }
  
  return element;
}

function AppRoutes({ showAlert }) {
  const token = localStorage.getItem("token");

  return (
    <Routes>
      {/* Portals Entry */}
      <Route path="/portals" element={!token ? <PortalSelector /> : <Navigate to="/" />} />
      
      {/* Authentication */}
      <Route path="/login" element={<Login showAlert={showAlert} />} />
      <Route path="/login/:role" element={<Login showAlert={showAlert} />} />
      <Route path="/signup" element={<Signup showAlert={showAlert} />} />
      <Route path="/signup/:role" element={<Signup showAlert={showAlert} />} />
      <Route path="/admin/login" element={<Navigate to="/login/admin" />} />
      
      <Route path="/auth/callback" element={<OAuthCallback showAlert={showAlert} />} />
      <Route path="/complete-profile" element={<PrivateRoute element={<CompleteProfile showAlert={showAlert} />} />} />
      <Route path="/" element={!token ? <Navigate to="/portals" /> : <NonAdminRoute element={<UserPanel showAlert={showAlert} />} />} />
      <Route path="/select-restaurant" element={<RestaurantSelection showAlert={showAlert} />} />
      <Route path="/info" element={<PrivateRoute element={<Info showAlert={showAlert} />} />} />
      <Route path="/forgot-password" element={<ForgotPassword showAlert={showAlert} />} />
      <Route path="/reset-password" element={<ResetPassword showAlert={showAlert} />} />
      <Route path="/table-reserve" element={<PrivateRoute element={<Elements stripe={stripePromise}><TableComponent showAlert={showAlert} /></Elements>} />}/>
      <Route path="/admin" element={<AdminRoute element={<Admin showAlert={showAlert} />} />} />
      <Route path="/admin/list" element={<AdminRoute element={<List showAlert={showAlert} />} />} />
      <Route path="/admin/add" element={<AdminRoute element={<Add showAlert={showAlert} />} />} />
      <Route path="/admin/edit-food/:id" element={<AdminRoute element={<Add showAlert={showAlert} />} />} />
      <Route path="/admin/all-users" element={<AdminRoute element={<UserData showAlert={showAlert} />} />} />
      <Route path="/admin/all-reviews" element={<AdminRoute element={<Reviews showAlert={showAlert} />} />} />
      <Route path="/admin/users/reviews/:userId" element={<AdminRoute element={<UserReviews />} />} />
      <Route path="/admin/admin-table" element={<AdminRoute element={<AdminTable showAlert={showAlert} />} />} />
      <Route path="/admin/slot/:slotNumber" element={<AdminRoute element={<SlotTable showAlert={showAlert} />} />} />
      <Route path="/admin/create-bill" element={<AdminRoute element={<CreateBill showAlert={showAlert} />} />} />
      <Route path="/admin/invoice" element={<AdminRoute element={<Invoices showAlert={showAlert} />} />} />
      <Route path="/admin/user/dash-board/:userId" element={<AdminRoute element={<UserDashBoard showAlert={showAlert} />} />} />
      <Route path="/admin/all-invoices" element={<AdminRoute element={<InvoiceList showAlert={showAlert} />} />} />
      <Route path="/admin/invoices/:invoiceId" element={<AdminRoute element={<InvoiceDetail showAlert={showAlert} />} />} />
      <Route path="/admin/invoices/edit/:invoiceId" element={<AdminRoute element={<EditInvoice showAlert={showAlert} />} />} />
      <Route path="/admin/users/invoice/:userId" element={<AdminRoute element={<UserInvoice />} />} />
      <Route path="/user/invoices" element={<PrivateRoute element={<UserInvoices />} />} />
      <Route path="/user/messages" element={<PrivateRoute element={<UserMessages />} />} />
      <Route path="/user/:invoiceId" element={<PrivateRoute element={<UserInvoiceDetail />} />} />
      <Route path="/admin/graph" element={<AdminRoute element={<Graph />} />} />
      <Route path="/admin/kitchen" element={<PrivateKitchenRoute element={<KitchenDisplay />} />} />
      <Route path="/admin/staff-attendance" element={<PrivateKitchenRoute element={<StaffAttendance showAlert={showAlert} />} />} />
      <Route path="/admin/chef-settings" element={<PrivateKitchenRoute element={<ChefSettings showAlert={showAlert} />} />} />
      <Route path="/admin/team" element={<AdminRoute element={<StaffManagement />} />} />
      <Route path="/admin/staff/attendance" element={<AdminRoute element={<Attendance showAlert={showAlert} />} />} />
      <Route path="/admin/staff/salary" element={<AdminRoute element={<SalarySlips showAlert={showAlert} />} />} />
      <Route path="/admin/staff/performance" element={<AdminRoute element={<Performance showAlert={showAlert} />} />} />
      <Route path="/admin/inventory" element={<AdminRoute element={<InventoryDashboard />} />} />
      <Route path="/admin/smart-prep" element={<AdminRoute element={<SmartPrep />} />} />

      <Route path="/food/:id" element={<NonAdminRoute element={<FoodDetail />} />} />
      <Route path="/About" element={<NonAdminRoute element={<About />} />} />
      <Route path="/Menu_Page" element={<NonAdminRoute element={<Menu_Page />} />} />
      <Route path="/all-foods" element={<NonAdminRoute element={<AllFoods />} />} />
      <Route path="/Recipes" element={<NonAdminRoute element={<AllFoods />} />} />
      <Route path='/Services' element={<NonAdminRoute element={<Services/>}/>} />
      <Route path='/BlogDetails' element={<NonAdminRoute element={<BlogDeatils/>}/>} />
      <Route path="/admin/settings" element={<AdminRoute element={<Settings />} />} />
      <Route path="/subscription/success" element={<AdminRoute element={<SubscriptionSuccess />} />} />
      <Route path="/subscription/cancel" element={<AdminRoute element={<SubscriptionCancel />} />} />
      <Route path="/admin/subscription/plans" element={<AdminRoute element={<PlanSelection />} />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default AppRoutes;
